import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadForm = ({ treatmentTitle, treatmentSlug }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setError("");
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          treatment: treatmentTitle,
          treatment_slug: treatmentSlug,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Kunne ikke sende. Prøv igjen.");
      }
      setStatus("success");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      setStatus("error");
      setError(err.message || "Noe gikk galt. Prøv igjen.");
    }
  };

  return (
    <section
      id="lead-form"
      className="py-20 md:py-28 bg-[#F7F6F3]"
      data-testid={`treatment-${treatmentSlug}-lead-form`}
    >
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="bg-white border border-[#E5E0DA] rounded-[2rem] md:rounded-[2.5rem] p-8 md:p-14"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#C4A981]"></span>
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#635D56]">
              Send henvendelse
            </span>
          </div>
          <h2 className="font-serif text-3xl md:text-5xl leading-[1.08] text-[#2C2C2A] tracking-tight font-light max-w-3xl">
            Vi tar kontakt for en <em className="italic text-[#4A4238]">personlig vurdering</em>.
          </h2>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-[#635D56] font-light leading-relaxed">
            Fyll ut skjemaet – vi tar kontakt for å bekrefte at {treatmentTitle.toLowerCase()} passer for deg og finne et tidspunkt som fungerer.
          </p>

          {status === "success" ? (
            <div
              className="mt-10 flex items-start gap-4 bg-[#EBE5DF]/60 border border-[#C4A981]/50 rounded-2xl p-6"
              data-testid="lead-form-success"
              role="status"
            >
              <span className="rounded-full bg-[#C4A981] text-[#F7F6F3] p-2 shrink-0">
                <Check size={16} strokeWidth={2} />
              </span>
              <div>
                <h3 className="font-serif text-xl text-[#2C2C2A]">Takk – vi har mottatt henvendelsen din.</h3>
                <p className="mt-2 text-sm text-[#635D56] font-light leading-relaxed">
                  Vi tar kontakt så snart som mulig for å bekrefte behandlingen og finne et tidspunkt som passer.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-10 space-y-6" data-testid="lead-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                <label className="block">
                  <span className="block text-[11px] uppercase tracking-[0.22em] text-[#635D56] mb-2">Navn *</span>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    className="w-full bg-transparent border-b border-[#D1C9C0] focus:border-[#4A4238] outline-none pb-3 text-base text-[#2C2C2A] font-light transition-colors"
                    placeholder="Fullt navn"
                    data-testid="lead-form-name"
                  />
                </label>
                <label className="block">
                  <span className="block text-[11px] uppercase tracking-[0.22em] text-[#635D56] mb-2">Telefon *</span>
                  <input
                    type="tel"
                    required
                    value={form.phone}
                    onChange={update("phone")}
                    className="w-full bg-transparent border-b border-[#D1C9C0] focus:border-[#4A4238] outline-none pb-3 text-base text-[#2C2C2A] font-light transition-colors"
                    placeholder="+47 000 00 000"
                    data-testid="lead-form-phone"
                  />
                </label>
              </div>

              <label className="block">
                <span className="block text-[11px] uppercase tracking-[0.22em] text-[#635D56] mb-2">E-post *</span>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  className="w-full bg-transparent border-b border-[#D1C9C0] focus:border-[#4A4238] outline-none pb-3 text-base text-[#2C2C2A] font-light transition-colors"
                  placeholder="din@epost.no"
                  data-testid="lead-form-email"
                />
              </label>

              <label className="block">
                <span className="block text-[11px] uppercase tracking-[0.22em] text-[#635D56] mb-2">Melding (valgfritt)</span>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  className="w-full bg-transparent border-b border-[#D1C9C0] focus:border-[#4A4238] outline-none pb-3 text-base text-[#2C2C2A] font-light transition-colors resize-none"
                  placeholder="Fortell oss litt om hva du ønsker hjelp med..."
                  data-testid="lead-form-message"
                />
              </label>

              <input type="hidden" value={treatmentTitle} readOnly />

              {status === "error" && (
                <div
                  className="text-sm text-[#8B2E2E] bg-[#F5E6E6] border border-[#8B2E2E]/30 rounded-xl px-4 py-3"
                  data-testid="lead-form-error"
                  role="alert"
                >
                  {error}
                </div>
              )}

              <div className="pt-4 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                <p className="text-xs text-[#635D56]/80 font-light max-w-md">
                  Ved å sende samtykker du til at Medskinby lagrer opplysningene og tar kontakt om {treatmentTitle.toLowerCase()}.
                </p>
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="group inline-flex items-center justify-between gap-6 bg-[#4A4238] text-[#F7F6F3] hover:bg-[#2C2C2A] disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-500 rounded-full pl-7 pr-3 py-3 text-xs uppercase tracking-[0.22em]"
                  data-testid="lead-form-submit"
                >
                  {status === "submitting" ? "Sender..." : "Send henvendelse"}
                  <span className="rounded-full bg-[#C4A981] p-2.5 group-hover:rotate-45 transition-transform duration-500">
                    <ArrowUpRight size={14} strokeWidth={1.5} />
                  </span>
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
